import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { ChecklistItem, DownloadFormPayload } from '../interfaces/checklist.model';
import { ERROR_MESSAGES } from '../shared/constants/error-message.constants';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { NotificationService } from '../shared/services/notification.service';
import { formatUrl } from '../shared/utils/format-url-util';
import { HttpRequestService } from '../shared/services/http-request.service';

@Injectable({
  providedIn: 'root'
})
export class CheckListService {
  private apiUrl = environment.apiUrl;
  constructor(private httpClient: HttpRequestService,
    private http: HttpClient,
    private errorHandler: GlobalErrorHandlerService,
    private notificationService: NotificationService,

  ) { }

  getChecklist(program_code: string, userId: string, isparticipant: boolean, isWriteRequest = false): Observable<any> {
    const url = formatUrl(
      isparticipant ? ENDPOINTS.GET_PARTICIPANT_CHECKLIST : ENDPOINTS.GET_PROVIDER_CHECKLIST,
      { program_code, userId },
      this.notificationService
    );
    return this.httpClient.get(url, isWriteRequest).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getChecklistForParticipantAssociation(program_code: string, providerId: string, participantId: string, providerType: string, isWriteRequest = false): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.GET_PARTICIPANT_ASSOCIATION_CHECKLIST,
      { program_code, providerId, participantId, providerType },
      this.notificationService
    );
    return this.httpClient.get(url, isWriteRequest).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  // ── Employer ⇄ Provider checklist (new accordions) ──────────────────────────
  // "Checklist for Providers" (current provider is an Employer): list the providers
  // linked to this employer and load/save each provider's checklist.
  getEmployerProviderList(program_code: string, employerId: string, isWriteRequest = false): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.EMPLOYERPROVIDER.GET_EMPLOYER_PROVIDER_LIST,
      { program_code, employerId },
      this.notificationService
    );
    return this.httpClient.get(url, isWriteRequest).pipe(
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  getEmployerProviderChecklist(program_code: string, employerId: string, providerId: string, userType: string, isWriteRequest = false): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.EMPLOYERPROVIDER.GET_EMPLOYER_PROVIDER_CHECKLIST,
      { program_code, employerId, providerId, userType },
      this.notificationService
    );
    return this.httpClient.get(url, isWriteRequest).pipe(
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  saveEmployerProviderChecklist(program_code: string, employerId: string, providerId: string, userType: string, payload: any): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.EMPLOYERPROVIDER.SAVE_EMPLOYER_PROVIDER_CHECKLIST,
      { program_code, employerId, providerId, userType },
      this.notificationService
    );
    return this.httpClient.post(url, payload, true).pipe(
      tap(() => this.notificationService.success('Success', ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS)),
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  // "Checklist for Employers" (current provider is NOT an employer): list the employers
  // linked to this provider and load/save each employer's checklist.
  getProviderEmployerList(program_code: string, providerId: string, isWriteRequest = false): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.PROVIDEREMPLOYER.GET_PROVIDER_EMPLOYER_LIST,
      { program_code, providerId },
      this.notificationService
    );
    return this.httpClient.get(url, isWriteRequest).pipe(
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  getProviderEmployerChecklist(program_code: string, providerId: string, employerId: string, userType: string, isWriteRequest = false): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.PROVIDEREMPLOYER.GET_PROVIDER_EMPLOYER_CHECKLIST,
      { program_code, providerId, employerId, userType },
      this.notificationService
    );
    return this.httpClient.get(url, isWriteRequest).pipe(
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  saveProviderEmployerChecklist(program_code: string, providerId: string, employerId: string, userType: string, payload: any): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.PROVIDEREMPLOYER.SAVE_PROVIDER_EMPLOYER_CHECKLIST,
      { program_code, providerId, employerId, userType },
      this.notificationService
    );
    return this.httpClient.post(url, payload, true).pipe(
      tap(() => this.notificationService.success('Success', ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS)),
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  getCheckListHistory(program_code: string, selectedParticipantId: string, entityId: string, providerType: string, checklistItemId: any, source: string, isAssociation = false): Observable<any> {
    let payload: any = {
      "id": entityId,
      "checklistItemId": checklistItemId
    }
    let apiUrl = '';
    if (isAssociation) {
      apiUrl = ENDPOINTS.GET_PARTICIPANT_ASSOCIATION_CHECKLIST_HISTORY;
      payload.participantId = +selectedParticipantId;
      payload.providerType = providerType;
      payload.providerId = entityId;
    } else if (source === 'provider') {
      apiUrl = ENDPOINTS.GET_PROVIDER_CHECKLIST_HISTORY;
    } else {
      apiUrl = ENDPOINTS.GET_PARTICIPANT_CHECKLIST_HISTORY;
    }
    const url = formatUrl(
      apiUrl,
      { program_code, providerType },
      this.notificationService
    );
    return this.httpClient.post(url, payload).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  // Form history for the Employer ⇄ Provider linked flow. Body: { id, provider_id, checklistItemId }
  // where id / provider_id are the provider's id.
  getLinkedFormHistory(program_code: string, mode: 'employerProvider' | 'providerEmployer', providerId: string, employerId: string, checklistItemId: any): Observable<any> {
    const apiUrl = mode === 'employerProvider'
      ? ENDPOINTS.EMPLOYERPROVIDER.GET_FORM_HISTORY
      : ENDPOINTS.PROVIDEREMPLOYER.GET_FORM_HISTORY;
    const payload = {
      providerId: Number(providerId),
      employerId: Number(employerId),
      checklistItemId,
      userType: mode === 'employerProvider' ? 'EMP' : 'IR' // userType is the "current" entity (employer or provider) for the history request
    };
    const url = formatUrl(apiUrl, { program_code }, this.notificationService);
    return this.httpClient.post(url, payload).pipe(
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  getPDFCheckList(program_code: string, userType: string, primaryLanguage: string, primaryId: string, providerId: string): Observable<any> {
    const payload = {
      "userType": `${userType}`,
      "primaryLanguage": `${primaryLanguage}`,
      "programCode": `${program_code}`,
      "participantId": `${primaryId}`,
      "providerId": `${providerId}`,
    };
    return this.httpClient.post(ENDPOINTS.GET_PDF_CHECKLIST, payload).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getRefreshForm(payload: any): Observable<any> {
    return this.httpClient.post(ENDPOINTS.GET_REFRESH_FORMS, payload).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          ERROR_MESSAGES.SUCCESS_REFRESH_FORMS
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getFormData(program_code: string,
    form: ChecklistItem,
    source: string,
    participantId: string, from = '', isAssociation = false, overrideEntities?: { userType: string; primaryId: string }[]): Observable<any> {
    let userDetails: any;
    if (overrideEntities && overrideEntities.length) {
      // Employer ⇄ Provider forms: caller supplies the exact provider/employer entities
      // (e.g. [{IR, prvPplId}, {EMP, empPplId}]); skip the participant PTC remap below.
      userDetails = overrideEntities;
    } else {
      userDetails = form.details.map(e => new Object({ userType: e.type, primaryId: e.pplId }));
      if (source == 'participant' && userDetails.filter((e: any) => e.userType == 'PTC').length == 0) {
        userDetails.push(new Object({ userType: 'PTC', primaryId: participantId }));
      } else if (isAssociation && source == 'provider') {
        if (userDetails && !userDetails.some((e: any) => e.userType == "PTC") && userDetails.some((e: any) => e.userType == "EMP")) {
          // Employer / Independent Provider associations: the participant is added as PTC, and the
          // employer entity is kept as well so the PDF can be populated with the employer's
          // details - e.g. [{IR, prvPplId}, {PTC, participantId}, {EMP, empPplId}].
          // Only one employer entity is sent: a participant can have more than one employer
          // association (the outgoing EOR stays active until the incoming one is approved), and
          // sending both makes the form render an employer section per entity.
          const empEntity = userDetails.find((d: any) => d.userType == 'EMP');
          userDetails = userDetails.filter((d: any) => d.userType !== 'EMP');
          userDetails.push({
            primaryId: participantId,
            userType: "PTC",
          });
          userDetails.push({
            primaryId: empEntity.primaryId,
            userType: "EMP",
          });
        }
      }
    }
    const payload: any = {
      formId: `${form.formMasterId}`,
      checkListItemId: `${form.checklistItemId}`,
      checkListItemDtlsId: `${form.checklistItemDetailId}`,
      source: `${source}`,
      programCode: `${program_code}`,
      languageCode: "ENG",
      prtcpntPrvdrIdAndTypes: userDetails
    };

    // Conditionally add checklistId if it is defined
    if (form.checklistId !== undefined && (from === 'history')) {
      payload.checklistId = `${form.checklistId}`;
    }

    return this.httpClient.post(ENDPOINTS.GET_PDF_DATA, payload).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  saveChecklist(program_code: string, userId: string, isParticipant: boolean, payload: any): Observable<any> {
    const url = formatUrl(
      isParticipant ? ENDPOINTS.SAVE_PARTICIPANT_CHECKLIST : ENDPOINTS.SAVE_PROVIDER_CHECKLIST,
      { program_code, userId },
      this.notificationService
    );
    return this.httpClient.post(url, payload, true).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  saveChecklistForParticipantAssociation(program_code: string, userId: string, participantId: number, providerType: string, payload: any): Observable<any> {
    const url = formatUrl(ENDPOINTS.SAVE_PROVIDER_PARTICIPANT_ASSOCIATION_CHECKLIST,
      { program_code, userId, participantId, providerType },
      this.notificationService
    );
    return this.httpClient.post(url, payload, true).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  saveParticipantProviderForms(program_code: string,
    id: string,
    isParticipant: boolean,
    payload: any,): Observable<any> {
    let url = '';
    if (!isParticipant) {
      url = formatUrl(ENDPOINTS.SAVE_PROVIDER_FORMS,
        { program_code, id },
        this.notificationService
      );
    } else {
      url = formatUrl(ENDPOINTS.SAVE_PARTICIPANT_FORMS,
        { program_code, id },
        this.notificationService
      );
    }

    return this.httpClient.post(url, payload, true).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  saveParticipantAssocaitionForms(program_code: string,
    participantId: number,
    providerId: string,
    providerType: string, payload: any,): Observable<any> {
    const url = formatUrl(ENDPOINTS.SAVE_PARTICIPANT_ASSOCATION_FORMS,
      { program_code, providerId, participantId, providerType },
      this.notificationService
    );

    return this.httpClient.post(url, payload, true).pipe(
      tap(() => {
        this.notificationService.success(
          'Success',
          ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  // Employer ⇄ Provider form save (sign). Both endpoints share the same URL shape
  // (/checklist/employer/{employerId}/provider/{providerId}?userType=).
  saveEmployerProviderForm(program_code: string, employerId: string, providerId: string, userType: string, payload: any): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.EMPLOYERPROVIDER.SAVE_EMPLOYER_PROVIDER_FORM,
      { program_code, employerId, providerId, userType },
      this.notificationService
    );
    return this.httpClient.post(url, payload, true).pipe(
      tap(() => this.notificationService.success('Success', ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS)),
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  saveProviderEmployerForm(program_code: string, employerId: string, providerId: string, userType: string, payload: any): Observable<any> {
    const url = formatUrl(
      ENDPOINTS.PROVIDEREMPLOYER.SAVE_PROVIDER_EMPLOYER_FORM,
      { program_code, employerId, providerId, userType },
      this.notificationService
    );
    return this.httpClient.post(url, payload, true).pipe(
      tap(() => this.notificationService.success('Success', ERROR_MESSAGES.SUCCESS_SAVE_PART_PROVIDERS_FORMS_COMMENTS)),
      catchError((err) => throwError(() => this.errorHandler.handleError(err)))
    );
  }

  downloadPDFForm(payload: DownloadFormPayload, fileName: string) {
    const headers = new HttpHeaders({
      'Accept': 'multipart/form-data'
    })
    this.http.post(ENDPOINTS.DOWNLOAD_PDF_FORM, payload, { headers: headers, responseType: 'blob', observe: 'response' })
      .subscribe((response: HttpResponse<Blob>) => {

        const contentDisposition = response.headers.get('Content-Disposition');
        if (response.body) {
          const blob = new Blob([response.body], { type: response.body.type });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      },
        error => {
          console.error('unable to open the file', error)
        })
  }

  getPDF(program_code: string, pdf_id: number, lang: string) {
    const url = formatUrl(ENDPOINTS.GET_PDF, { program_code, pdf_id, lang });
    const headers = new HttpHeaders({
      // 'Accept': 'multipart/form-data',
      'content-type': 'application/json'
    })
    return this.http.get(url, { headers: headers, responseType: 'blob', observe: 'response' });
    // this.httpClient.get(url, { headers: headers, responseType: 'blob', observe: 'response' }).subscribe((response: HttpResponse<Blob>) => {

    //   const contentDisposition = response.headers.get('Content-Disposition');
    //   const fileName = 'test.pdf';
    //   if(response.body) {
    //     const blob = new Blob([response.body], {type: response.body.type});
    //     const url = window.URL.createObjectURL(blob);
    //   }
    //   },
    //     error => {
    //       console.error('unable to open the file', error)
    //     })
  }


  getFormQRCode(payload: any) {
    const headers = new HttpHeaders({
      'content-type': 'application/json'
    })
    return this.http.post(ENDPOINTS.GET_PDF_QR_CODE, payload, { headers: headers, responseType: 'blob', observe: 'response' });
  }
}
